import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useStyleMode } from '@/features/restaurant/useStyleMode';

interface Props {
  messages: string[];
}

const MIN_ITEMS_PER_SEGMENT = 48;
// Reading speed of the banner, in pixels per second.
const SCROLL_SPEED_PX_PER_SEC = 40;
// Used until the track has been measured (and in environments without layout,
// e.g. jsdom). Matches the speed above for a typical emoji-only segment.
const FALLBACK_DURATION_SECONDS = 120;

export default function Marquee({ messages }: Props) {
  const { t } = useTranslation();
  const premium = useStyleMode() === 'premium';
  const trackRef = useRef<HTMLDivElement>(null);
  const [trackWidth, setTrackWidth] = useState(0);

  // Re-measure on mount, on font load and on resize — all of which change how
  // wide the track is, and therefore how long one lap must take.
  useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const measure = () => setTrackWidth(el.scrollWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [messages.join('|'), premium]);

  if (messages.length === 0) return null;
  const repetitions = Math.max(2, Math.ceil(MIN_ITEMS_PER_SEGMENT / messages.length));
  const segment = Array.from({ length: repetitions }, () => messages).flat();
  const items = [...segment, ...segment];
  // One lap travels exactly one segment (the track holds the segment twice), so
  // the duration follows the measured pixel width rather than the item count.
  // Counting items made the speed depend on how long the messages are: a promo
  // line such as "🎁 −10 % dès 10 €" builds a segment several times wider than
  // an emoji-only one, and covered it in the same time — i.e. far too fast.
  const segmentWidth = trackWidth / 2;
  const durationSeconds =
    segmentWidth > 0 ? segmentWidth / SCROLL_SPEED_PX_PER_SEC : FALLBACK_DURATION_SECONDS;
  return (
    <div
      className={`relative overflow-hidden flex items-center group ${
        premium
          ? 'bg-paper border-b border-[var(--border-color)] h-[40px]'
          : 'bg-mint border-b-2 border-ink h-[40px] sm:h-[46px]'
      }`}
      role="region"
      aria-label={t('header.announcements')}
    >
      <div
        ref={trackRef}
        data-marquee-track
        className={`flex w-max items-center animate-[marquee_var(--marquee-duration)_linear_infinite] [animation-play-state:running] group-hover:[animation-play-state:paused] whitespace-nowrap font-display text-ink uppercase ${
          premium ? 'text-[12px] tracking-[0.22em]' : 'text-[15px] sm:text-[18px] tracking-[0.06em]'
        }`}
        style={{ '--marquee-duration': `${durationSeconds}s` } as CSSProperties}
      >
        {items.map((m, i) => (
          <span key={i} className="flex shrink-0 items-center whitespace-nowrap">
            <span className={`whitespace-nowrap ${premium ? 'px-9' : 'px-5 sm:px-7'}`}>{m}</span>
            <span
              aria-hidden="true"
              className={
                premium
                  ? 'text-[var(--mint)] text-[7px] leading-none'
                  : 'font-display text-hot-pink text-[16px] sm:text-[20px] leading-none -translate-y-[1px]'
              }
            >
              {premium ? '◆' : '→'}
            </span>
          </span>
        ))}
      </div>
      <style>{`
        @keyframes marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        @media (hover: none) {
          [data-marquee-track] { animation-play-state: running !important; }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-marquee-track] { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
